import { useCallback, useState } from 'react';

import shuffle from '../lib/shuffle.js';

const MAX_CIRCLE_WIDTH_AND_HEIGHT = 130;
const MIN_CIRCLE_WIDTH_AND_HEIGHT = 80;

const setDuplicateValuesToSameSize = (data, minWidthAndHeight) => {
  const dataMap = data.reduce((all, item) => {
    all[item.value] = item.width_and_height;
    return all;
  }, {});

  return data.map((item) => {
    if (dataMap[item.value]) {
      item.width_and_height = dataMap[item.value];
      if (item.value === 0) {
        item.width_and_height = minWidthAndHeight;
      }
    }
    return item;
  });
};
const useBuildCirclePackerGraph = ({ data, includePercentages, customMaxWidthAndHeight, customMinWidthAndHeight }) => {
  const [circles, setCircles] = useState(() => []);
  const [dimensions, setDimensions] = useState(null);
  const maxWidthAndHeight = customMaxWidthAndHeight ? customMaxWidthAndHeight : MAX_CIRCLE_WIDTH_AND_HEIGHT;
  const minWidthAndHeight = customMinWidthAndHeight ? customMinWidthAndHeight : MIN_CIRCLE_WIDTH_AND_HEIGHT;

  const handleBuildCirclePackerGraph = useCallback(() => {
    if (dimensions !== null) {
      const containerWidth = dimensions;
      let currentSize = minWidthAndHeight;
      const determineCircleSize = (original_arr, item, i) => {
        const ceil = original_arr.length;
        const floor = 0;
        let iterations = Math.ceil(maxWidthAndHeight / ceil);
        if (iterations > 50) {
          iterations = iterations / 1.5;
        }
        if (i === floor) {
          return minWidthAndHeight;
        }
        if (i === ceil) {
          return maxWidthAndHeight;
        }
        currentSize = currentSize + iterations;
        return currentSize;
      };

      if (data !== 'failed' && (data || []).length) {
        let ordered = (data || []).sort((a, b) => (a || {}).value - (b || {}).value);

        if (includePercentages) {
          const total = ordered.reduce((all, item) => {
            all += parseInt(item.value);
            return all;
          }, 0);

          ordered.forEach((item) => {
            let divide = parseInt(item.value) / total;
            let multiply = divide * 100;
            let rounded = parseFloat(multiply).toFixed(0);
            item.percentage = `${rounded}%`;
          });
        }

        const orderedCount = ordered.length;
        const circleSizes = ordered.map((item, i) => {
          return { width_and_height: determineCircleSize(ordered, item, i), ...item };
        });
        const negativeMarginsValue = orderedCount * 32;
        const allCirclesWidth = circleSizes.reduce((all, item) => (all += item.width_and_height), 0);
        const allCirclesWidthCalculatedWithNegativeMargin = allCirclesWidth - negativeMarginsValue - 32;
        const percentageSpaceUsedPerCircleTotal = allCirclesWidthCalculatedWithNegativeMargin / orderedCount;
        const percentageSpaceUsedPerCircle = (percentageSpaceUsedPerCircleTotal / containerWidth) * 100;
        let sliceValue = 0;
        if (percentageSpaceUsedPerCircle >= 18) {
          sliceValue = 1;
        }
        if (percentageSpaceUsedPerCircle >= 22) {
          sliceValue = 2;
        }
        if (percentageSpaceUsedPerCircle >= 24) {
          sliceValue = 3;
        }
        if (percentageSpaceUsedPerCircle >= 26) {
          sliceValue = 4;
        }
        if (percentageSpaceUsedPerCircle >= 28) {
          sliceValue = orderedCount;
        }
        if (sliceValue !== orderedCount && percentageSpaceUsedPerCircle * orderedCount > 100) {
          sliceValue = sliceValue += 2;
        }
        let final = [];
        // Slice items and add to 'Other' bubble
        if (sliceValue > 0) {
          const otherBubbleValues = circleSizes.slice(0, sliceValue);
          const remainingValues = circleSizes.slice(sliceValue, circleSizes.length);
          const otherBubbleSingleValue = otherBubbleValues.reduce((all, item) => {
            const { value, name } = item;
            if (!all.hasOwnProperty('value')) {
              all.value = 0;
            }
            if (!all.other_items) {
              all.other_items = [];
            }
            all.value += parseInt(item.value);
            all.name = 'Other Values';
            all.other_items.push({ value, name });
            return all;
          }, {});
          final = [{ ...otherBubbleSingleValue }, ...remainingValues].map((item, i) => {
            return { width_and_height: remainingValues.length === 0 ? 210 : determineCircleSize(ordered, item, i), ...item };
          });
          const shuffled = shuffle(final);
          setCircles(setDuplicateValuesToSameSize(shuffled, minWidthAndHeight));
          return;
        }
        final = circleSizes.map((item, i) => {
          return { width_and_height: final.length === 1 ? 210 : determineCircleSize(ordered, item, i), ...item };
        });
        const shuffled = shuffle(final);
        setCircles(setDuplicateValuesToSameSize(shuffled, minWidthAndHeight));
        return;
      }
    }
  }, [data, dimensions]);
  return { handleBuildCirclePackerGraph, circles, setDimensions };
};

export { useBuildCirclePackerGraph };
